import { useEffect, useRef, useCallback } from "react";

// Reliable smart polling: runs fn on an interval, pauses when the tab is hidden,
// refreshes immediately when the tab becomes visible again. Backs off on errors.
export function usePoll(fn, interval = 5000, deps = []) {
  const savedFn = useRef(fn);
  savedFn.current = fn;
  const failRef = useRef(0);
  const timerRef = useRef(null);

  const run = useCallback(async () => {
    if (document.hidden) return;
    try { await savedFn.current(); failRef.current = 0; }
    catch { failRef.current = Math.min(failRef.current + 1, 5); }
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      await run();
      if (stopped) return;
      const backoff = failRef.current > 0 ? interval * Math.pow(2, failRef.current) : interval;
      timerRef.current = setTimeout(tick, backoff);
    };
    run(); // immediate
    timerRef.current = setTimeout(tick, interval);

    const onVis = () => { if (!document.hidden) { clearTimeout(timerRef.current); run(); timerRef.current = setTimeout(tick, interval); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { stopped = true; clearTimeout(timerRef.current); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
